import { useMemo } from "react";
import type { CaseCopy } from "@/content/types";
import type { ResultResponse, Finding, Bottleneck } from "@/lib/types";
import { canonicalSeverity } from "@/lib/format";
import { FiveStepDiagram } from "./FiveStepDiagram";
import "./dossier.css";

type Props = {
  copy: CaseCopy;
  result: ResultResponse;
};

export function Dossier({ copy, result }: Props) {
  const findings = result.insights.findings;
  const dur = result.analytics.case_duration;
  const topBottleneck: Bottleneck | undefined = result.analytics.bottlenecks_top10_by_p95[0];

  const [mastLabel, mastDate] = splitMasthead(copy.mastheadIssue);

  return (
    <main className="main-col stagger dossier" key={copy.slug}>
      {/* Masthead strip */}
      <div className="mast">
        <div className="lhs">FlowLens <span>·</span> Data Essays</div>
        <div className="rhs">{mastLabel}<em> · </em>{mastDate}</div>
      </div>

      {/* 1. A note from the analyst */}
      <section className="intro" id="sec-note">
        <div className="salute">A note from the analyst</div>
        <p>
          Hi — I'm Carlos Altamirano. This is a showcase of <em>process mining</em>. Companies
          run on processes: how a loan gets approved, how a ticket gets resolved, how a fine
          gets collected. Most of the time, the way a company <em>thinks</em> a process works
          and the way it <em>actually</em> works are two different things. Process mining closes
          that gap. It reads the timestamps every system already writes — who did what, and
          when — and rebuilds the real shape of the work from them. <b>{copy.noteTagline}</b>
        </p>
      </section>

      {/* 2. Source card */}
      <aside className="src-card">
        <div className="lbl">Where this came from</div>
        <p>{copy.sourceCard}</p>
      </aside>

      {/* 3. Hero */}
      <section className="hero" id="sec-hero">
        <div className="kicker">{copy.hero.kicker}</div>
        <h1>{copy.hero.title}</h1>
        <p className="dek">{copy.hero.dek}</p>
      </section>

      {/* Dot grid */}
      {copy.dotGrid && <DotGrid spec={copy.dotGrid} />}

      {/* Lede drop-cap */}
      <p className="lede">{copy.lede}</p>

      {/* 4. Headline finding */}
      <section className="finding-hd" id="sec-headline">
        <div className="lbl" style={{ justifyContent: "center" }}>
          {copy.headlineFinding.label}
        </div>
        <h2>{copy.headlineFinding.h2}</h2>
        <p>{copy.headlineFinding.body}</p>
      </section>

      <div className="issue-grid">
        {copy.headlineFinding.issues.map((it, i) => (
          <div key={i} className="issue">
            <div className="tag">{renderIssueTag(it.tag)}</div>
            <div className="big">
              {it.big}
              {it.unit && <span className="unit">{" "}{it.unit}</span>}
            </div>
            <div className="ev">{it.ev}</div>
          </div>
        ))}
      </div>

      {/* Mini bar chart */}
      <MiniBarChart
        p50={dur.p50_days}
        p90={dur.p90_days}
        p95={dur.p95_days}
        max={dur.max_days}
        mean={dur.mean_days}
      />

      {/* 5. Section 02 — diagram */}
      <section className="diagram-wrap" id="sec-02">
        <div className="lbl">{copy.sectionTwo.label}</div>
        <FiveStepDiagram config={copy.sectionTwo.diagram} />
        {copy.sectionTwo.sparkline && (
          <Sparkline
            title={copy.sectionTwo.sparkline.title}
            body={copy.sectionTwo.sparkline.body}
            bottleneck={topBottleneck}
          />
        )}
      </section>

      {/* 6. Section 03 — findings grid */}
      <div className="lbl" id="sec-03">
        {copy.sectionThree.labelFromCount(findings.length)}
      </div>
      <section className="findings">
        {findings.map((f, i) => (
          <FindingCard key={i} finding={f} index={i + 1} />
        ))}
      </section>

      {/* 7. Section 04 — How this was built (fixed) */}
      <StackMethod />

      <hr className="thin-rule" />

      {/* 8. Section 05 — Recommendations */}
      <section id="sec-05">
        <div className="lbl">{copy.sectionFive.label}</div>
        <div className="recs-hd">
          <h2>{copy.sectionFive.h2}</h2>
          <p className="pull">{copy.sectionFive.pull}</p>
        </div>
        <div className="recs">
          {copy.sectionFive.recs.map((r, i) => (
            <div key={i} className="rec-row">
              <div className="tag">{r.tag}</div>
              <div className="act">{r.act}</div>
              <div className="impact">{r.impact}</div>
            </div>
          ))}
        </div>
        <p className="close">{copy.sectionFive.close}</p>
        <div className="signoff">— <b>Carlos Altamirano</b> · Vancouver · April 2026</div>
      </section>
    </main>
  );
}

/* ---------- Helpers ---------- */

function splitMasthead(full: string): [string, string] {
  const i = full.indexOf("·");
  if (i < 0) return [full.trim(), ""];
  return [full.slice(0, i).trim(), full.slice(i + 1).trim()];
}

function renderIssueTag(tag: string): React.ReactNode {
  const idx = tag.indexOf("·");
  if (idx < 0) return tag;
  return (
    <>
      {tag.slice(0, idx).trim()} <b>·{tag.slice(idx + 1)}</b>
    </>
  );
}

/* ---------- Dot grid ---------- */
function DotGrid({ spec }: { spec: { total: number; bad: number; caption: React.ReactNode } }) {
  const dots = useMemo(() => {
    let seed = 7;
    const rand = () => ((seed = (seed * 9301 + 49297) % 233280), seed / 233280);
    const badSet = new Set<number>();
    while (badSet.size < spec.bad) badSet.add(Math.floor(rand() * spec.total));
    return Array.from({ length: spec.total }, (_, i) => badSet.has(i));
  }, [spec.total, spec.bad]);

  return (
    <div className="dotgrid-wrap">
      <div className="dotgrid">
        {dots.map((isBad, i) => (
          <span key={i} className={isBad ? "bad" : undefined} />
        ))}
      </div>
      <div className="caption">{spec.caption}</div>
    </div>
  );
}

/* ---------- Mini bar chart ---------- */
type ChartProps = { p50: number; p90: number; p95: number; max: number; mean: number };
function MiniBarChart({ p50, p90, p95, max, mean }: ChartProps) {
  /* Sqrt scale: compresses long tails so all four bars read. MAX always
     fills the chart (sqrt(max)/sqrt(max)=1), and the P-bars scale as
     √value/√max — roughly mirroring perceptual magnitude, not raw days. */
  const H = 146;
  const MAX_AXIS = Math.max(max, 1);
  const root = Math.sqrt(MAX_AXIS);
  const hP50 = Math.max(8,  Math.round((Math.sqrt(p50) / root) * H));
  const hP90 = Math.max(14, Math.round((Math.sqrt(p90) / root) * H));
  const hP95 = Math.max(20, Math.round((Math.sqrt(p95) / root) * H));
  const hMax = H;
  const y = (h: number) => 160 - h;

  return (
    <div className="minichart">
      <div className="caption">
        <b>Case duration, days</b>
        Percentile distribution of the log. The tail drags; the median stays put.
      </div>
      <svg viewBox="0 0 640 180" style={{ flex: 1 }}>
        <line x1={0} y1={160} x2={640} y2={160} stroke="#1A1C20" strokeWidth={1} />

        <rect x={20}  y={y(hP50)} width={90} height={hP50} fill="#1E6B5A" />
        <text x={65}  y={y(hP50) - 6} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize={12} fill="#1A1C20">{p50.toFixed(2)}</text>
        <text x={65}  y={175} textAnchor="middle" fontFamily="IBM Plex Sans" fontSize={10} letterSpacing={2} fill="#45474B">P50</text>

        <rect x={140} y={y(hP90)} width={90} height={hP90} fill="#D48C1E" />
        <text x={185} y={y(hP90) - 6} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize={12} fill="#1A1C20">{p90.toFixed(2)}</text>
        <text x={185} y={175} textAnchor="middle" fontFamily="IBM Plex Sans" fontSize={10} letterSpacing={2} fill="#45474B">P90</text>

        <rect x={260} y={y(hP95)} width={90} height={hP95} fill="#A83625" />
        <text x={305} y={y(hP95) - 6} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize={12} fill="#1A1C20">{p95.toFixed(2)}</text>
        <text x={305} y={175} textAnchor="middle" fontFamily="IBM Plex Sans" fontSize={10} letterSpacing={2} fill="#45474B">P95</text>

        <rect x={380} y={y(hMax)} width={90} height={hMax} fill="#1A1C20" />
        <text x={425} y={y(hMax) - 6} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize={12} fill="#1A1C20">{max.toFixed(2)}</text>
        <text x={425} y={175} textAnchor="middle" fontFamily="IBM Plex Sans" fontSize={10} letterSpacing={2} fill="#45474B">MAX</text>

        <line x1={500} y1={40} x2={500} y2={160} stroke="#45474B" strokeDasharray="3,3" />
        <text x={508} y={46}  fontFamily="IBM Plex Sans" fontSize={10} letterSpacing={2} fill="#45474B">MEAN</text>
        <text x={508} y={60}  fontFamily="IBM Plex Mono" fontSize={12} fill="#1A1C20">{mean.toFixed(2)} d</text>
        <text x={508} y={108} fontFamily="Literata"       fontStyle="italic" fontSize={12} fill="#45474B">5% of cases</text>
        <text x={508} y={124} fontFamily="Literata"       fontStyle="italic" fontSize={12} fill="#45474B">take &gt; {p95.toFixed(0)} days</text>
        <text x={508} y={140} fontFamily="Literata"       fontStyle="italic" fontSize={12} fill="#45474B">— and one took {max.toFixed(0)}.</text>
      </svg>
    </div>
  );
}

/* ---------- Sparkline box ---------- */
function Sparkline({ title, body, bottleneck }: {
  title: string;
  body: React.ReactNode;
  bottleneck?: Bottleneck;
}) {
  return (
    <div className="spark-wrap">
      <div>
        <h4>{title}</h4>
        <p>{body}</p>
      </div>
      <svg viewBox="0 0 340 100" style={{ width: "100%", height: 100 }}>
        <line x1={0} y1={80} x2={340} y2={80} stroke="#E4DFD3" />
        <path
          d="M 0,80 L 10,78 L 20,60 L 30,30 L 45,18 L 60,22 L 80,32 L 100,42 L 120,48 L 140,54 L 160,60 L 180,64 L 200,68 L 220,70 L 240,72 L 260,74 L 280,76 L 300,77 L 320,78 L 340,80 Z"
          fill="#F0E1C1"
          stroke="#D48C1E"
          strokeWidth={1.4}
        />
        <line x1={50} y1={18} x2={50} y2={80} stroke="#1E6B5A" strokeDasharray="3,3" />
        <text x={54} y={14} fontFamily="IBM Plex Mono" fontSize={10} fill="#1E6B5A">
          median {bottleneck ? bottleneck.p50_days.toFixed(2) : "—"} d
        </text>
        <line x1={150} y1={56} x2={150} y2={80} stroke="#A83625" />
        <text x={154} y={54} fontFamily="IBM Plex Mono" fontSize={10} fill="#A83625">
          p95 {bottleneck ? bottleneck.p95_days.toFixed(1) : "—"} d
        </text>
        <text x={240} y={70} fontFamily="Literata" fontStyle="italic" fontSize={11} fill="#7A7B7E">
          …tail to {bottleneck ? bottleneck.max_days.toFixed(0) : "—"} d
        </text>
      </svg>
    </div>
  );
}

/* ---------- Finding card ---------- */
function FindingCard({ finding, index }: { finding: Finding; index: number }) {
  const sev = canonicalSeverity(finding.severity);
  const dotClass = sev === "critical" || sev === "high" ? "high"
                 : sev === "medium"   ? "med"
                 : "low";
  const sevLabel = sev === "critical" ? "Critical"
                 : sev === "high"     ? "High"
                 : sev === "medium"   ? "Medium"
                 : sev === "low"      ? "Low"
                 : "Info";

  return (
    <div className="fnd" data-severity={sev}>
      <div className="fnd-num">{index.toString().padStart(2, "0")}</div>
      <div className="sev">
        <span className={`dot ${dotClass}`}></span>
        {sevLabel}
      </div>
      <h3>{finding.title}</h3>
      <p>{finding.finding}</p>
      <div className="action">
        <b>Action</b>
        {finding.action}
      </div>
    </div>
  );
}

/* ---------- Stack + method (section 04 — FIXED across all cases) ---------- */
function StackMethod() {
  return (
    <section id="sec-04">
      <div className="lbl">· 04 · How this was built · Stack + method</div>
      <p className="build-sub">Each choice, with what it replaced and why it won.</p>

      <div className="layer l1">
        <div className="ln">01</div>
        <div>
          <div className="name">Ingest strategy</div>
          <div className="title">Streamed over loaded.</div>
          <div className="decision">
            <span className="chose">polars streaming + pm4py canonicalisation</span>
            <span className="rej">pandas read_csv (whole file into RAM)</span>
          </div>
          <div className="tools">
            1.2M rows → O(chunk) memory<br />
            case-id / activity / timestamp enforced at ingest
          </div>
        </div>
        <div className="body">
          Pandas in one shot works on 150k cases, fails on BPI 2017. Streaming is the only
          option that survives the scale target; every other layer downstream assumes
          polars-safe memory.
        </div>
      </div>

      <div className="layer l2">
        <div className="ln">02</div>
        <div>
          <div className="name">Process representation</div>
          <div className="title">DFG first, Petri second.</div>
          <div className="decision">
            <span className="chose">directly-follows graph as primary</span>
            <span className="rej">Petri net as the only representation</span>
          </div>
          <div className="tools">
            pm4py.discover_dfg<br />
            pm4py.discover_petri (kept for soundness checks)
          </div>
        </div>
        <div className="body">
          DFGs are what the LLM can reason over and what a human can read. Petri nets are
          computed in parallel but never shown — they live for validation, not for
          communication.
        </div>
      </div>

      <div className="layer l3">
        <div className="ln">03</div>
        <div>
          <div className="name">Analytics density</div>
          <div className="title">Compute one layer more than the LLM can derive.</div>
          <div className="decision">
            <span className="chose">pre-compute edge sojourn p50/p95/max + variant clustering</span>
            <span className="rej">hand the LLM the raw event log and hope</span>
          </div>
          <div className="tools">
            fast-vs-slow segmentation<br />
            variant clustering · case duration distribution
          </div>
        </div>
        <div className="body">
          LLMs are bad at percentiles and great at narrative. Splitting the work this way
          means the numbers are deterministic and the prose is cheap.
        </div>
      </div>

      <div className="layer l4">
        <div className="ln">04</div>
        <div>
          <div className="name">Model selection</div>
          <div className="title">Gemini 3 Flash Preview over Qwen 3.6 Plus.</div>
          <div className="decision">
            <span className="chose">google/gemini-3-flash-preview · 6.70s · $0.00558</span>
            <span className="rej">qwen-3.6-plus (98.57s · $0.01220 · reasoning-heavy)</span>
            <span className="rej">minimax-m2.7 (148s · content=None crash · unstable)</span>
          </div>
          <div className="tools">
            benchmarked A / B / C on Road Traffic 150k · 3 runs each<br />
            Qwen retained as transparent fallback
          </div>
        </div>
        <div className="body">
          Same prompt, same 561k-event DFG abstraction, 3 runs each, wall-clock end-to-end.
          Gemini 3 Flash chosen on <b>14.7× lower latency</b> (6.70s vs 98.57s) and{" "}
          <b>54% lower cost per call</b> ($0.00558 vs $0.01220). Qwen kept as fallback on HTTP
          and parse errors so a pricing or rate-limit change on the <code>preview</code> tag
          never breaks the pipeline.
        </div>
      </div>

      <div className="layer l5">
        <div className="ln">05</div>
        <div>
          <div className="name">Execution model</div>
          <div className="title">Celery async from day one.</div>
          <div className="decision">
            <span className="chose">FastAPI + Celery + Redis (Upstash)</span>
            <span className="rej">synchronous endpoint (6s Gemini → 90s Qwen if fallback)</span>
          </div>
          <div className="tools">
            Postgres (Neon) · Cloudflare R2<br />
            Alembic migrations · structured JSON result
          </div>
        </div>
        <div className="body">
          Even at 6s the sync path dies the moment the fallback kicks in. Celery from commit 1
          makes the request / work separation non-negotiable — no "temporary sync mode" to
          refactor out later.
        </div>
      </div>
    </section>
  );
}

